import { useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/styles';

import { a, useSprings } from 'react-spring';
import { useGesture } from 'react-use-gesture';
import { Link } from 'react-router-dom';
import map from 'lodash/fp/map';
import clamp from 'lodash/fp/clamp';

import BlockLayout from 'Blocks/BlockLayout';
import Typography from 'Components/Typography';
import { breakpoints } from 'Components/ThemeProvider';
import Button from 'Components/LegacyButton';
import useMedia from 'utils/useMedia';

const useStyles = makeStyles(
  theme => ({
    quotesContainer: {
      marginTop: 110,
      marginBottom: theme.spacing.s72,
      display: 'grid',
      gridTemplateColumns: '1fr 1fr 1fr',
      gridColumnGap: '30px',
      [theme.breakpoints.match('sm')]: {
        width: '100%',
        minHeight: 360,
      },
      [theme.breakpoints.match('md')]: {
        width: '100%',
        minHeight: 360,
      },
    },
    quoteContainer: {
      backgroundColor: 'white',
      padding: '57px 24px 48px',
      position: 'relative',
      width: '100%',
      flexGrow: 1,
      '& span': {
        [theme.breakpoints.match('md')]: {
          fontSize: 22,
        },
      },
    },
    wrapper: {
      display: 'flex',
      flexFlow: 'column',
      justifyContent: 'space-between',
      [theme.breakpoints.match('sm')]: {
        width: 290,
        margin: '0 0px',
        position: 'absolute',
        willChange: 'transform',
        minHeight: 373,
      },
      [theme.breakpoints.match('md')]: {
        width: 290,
        position: 'absolute',
        willChange: 'transform',
        minHeight: 373,
      },
    },
    informations: {
      backgroundColor: '#fbf6f2',
      display: 'flex',
    },
    illustration: {
      backgroundColor: 'grey',
      width: 109,
      height: 109,
      flexGrow: 1,
      '& img': {
        width: '100%',
        height: '100%',
      },
      [theme.breakpoints.match('sm')]: {
        height: 138,
      },
      [theme.breakpoints.match('md')]: {
        height: 138,
      },
    },
    text: {
      padding: '20px',
      flexShrink: 20,
    },
    picture: {
      width: 70,
      height: 70,
      position: 'absolute',
      borderRadius: '100%',
      top: '-35px',
      left: '50%',
      transform: 'translateX(-50%)',
      overflow: 'hidden',
      '& img': {
        width: '100%',
      },
    },

    fullWidth: {
      width: '100%',
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: '#ededf3',
      [theme.breakpoints.match('sm')]: {
        padding: `${theme.spacing.s72}px 0`,
        '& a': {
          margin: 'auto',
          display: 'block',
        },
      },
      [theme.breakpoints.match('md')]: {
        padding: `${theme.spacing.s72}px 0`,
        '& a': {
          margin: 'auto',
          display: 'block',
        },
      },
    },
  }),
  { name: 'CustomersReviews' }
);

const gutterSize = 20;
const calculatedGutter = cardIndex => (cardIndex + 1) * gutterSize;
const cardWidth = 290;

const CustomersReviews = ({ content }) => {
  const classes = useStyles();
  const { title, customers, ctaLabel } = content;

  const isTablet = useMedia(`(max-width: ${breakpoints.md - 1}px)`);
  const index = useRef(0);
  // only used for mobile
  const [cards, setCards] = useSprings(customers.length, i => ({
    x: i * cardWidth + calculatedGutter(i),
  }));
  // function that get the touch and mouse events and translate them into 'custom events'
  const bind = useGesture(({ down, direction: [xDir], distance, cancel }) => {
    // When down and swipping update index of my slider to 1 or -1
    if (down && distance > 100) {
      cancel((index.current = clamp(0, customers.length - 1, index.current + (xDir > 0 ? -1 : 1))));

      if (window.heap && window.heap.track) {
        window.heap.track(`Customer card ${index.current + 1} viewed`);
      }
    }
    setCards(i => {
      const x = (i - index.current) * cardWidth + calculatedGutter(i);
      return { x };
    });
  });

  return isTablet ? (
    <section className={classes.fullWidth}>
      <Typography align="center" paragraph variant="mono2">
        {title}
      </Typography>
      <div className={classes.quotesContainer}>
        {cards.map(({ x, display }, i) => {
          return (
            <a.div
              key={customers[i].name}
              className={classes.wrapper}
              style={{
                display,
                transform: x.interpolate(xAxis => `translate3d(${xAxis}px,0,0)`),
              }}
              {...bind()}
            >
              <div className={classes.quoteContainer}>
                <div className={classes.picture}>
                  <img alt={customers[i].name} src={customers[i].picture} />
                </div>
                <Typography align="center" variant="h2r">
                  {customers[i].quote}
                </Typography>
              </div>
              <div className={classes.informations}>
                <div className={classes.illustration}>
                  <img
                    alt={`${customers[i].name} routine`}
                    src={customers[i].routineIllustrationMobile}
                  />
                </div>
                <div className={classes.text}>
                  <Typography align="left" variant="p3">
                    {`${customers[i].name} ${customers[i].description}`}
                    {customers[i].usedProducts}
                  </Typography>
                </div>
              </div>
            </a.div>
          );
        })}
      </div>
      <Button Component={Link} to="/reviews" variant="noir">
        {ctaLabel}
      </Button>
    </section>
  ) : (
    <BlockLayout backgroundColor="#ededf3" contentAlign="center" contentWidth="veryWide">
      <div>
        <Typography align="center" paragraph variant="mono2">
          {title}
        </Typography>
        <div className={classes.quotesContainer}>
          {map(
            ({ quote, name, description, usedProducts, picture, routineIllustration }) => (
              <div key={name} className={classes.wrapper}>
                <div className={classes.quoteContainer}>
                  <div className={classes.picture}>
                    <img alt={name} src={picture} />
                  </div>
                  <Typography variant="h2r">{quote}</Typography>
                </div>
                <div className={classes.informations}>
                  <div className={classes.illustration}>
                    <img alt={`${name} routine`} src={routineIllustration} />
                  </div>
                  <div className={classes.text}>
                    <Typography align="left" variant="p3">
                      {`${name} ${description}`}
                      {usedProducts}
                    </Typography>
                  </div>
                </div>
              </div>
            ),
            customers
          )}
        </div>
        <Button
          Component={Link}
          data-click="reviews"
          data-from="block-customer-reviews"
          to="/reviews"
          variant="noir"
        >
          {ctaLabel}
        </Button>
      </div>
    </BlockLayout>
  );
};

CustomersReviews.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string.isRequired,
    customers: PropTypes.arrayOf(PropTypes.object).isRequired,
    ctaLabel: PropTypes.string.isRequired,
  }).isRequired,
};

export default CustomersReviews;
